import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/f322174fe77985532ed178684e9cde9f/7b841d10159b36d89f70d971de020396.png" alt="4 Leaf Trading" className="h-16 w-auto" />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a
                href="#availability"
                className={`font-medium transition-colors ${
                  scrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
                }`}
              >
                Inventory
              </a>
              <a
                href="#about"
                className={`font-medium transition-colors ${
                  scrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
                }`}
              >
                About
              </a>
              <Link
                to="/visit"
                className={`font-medium transition-colors ${
                  scrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
                }`}
              >
                Visit Us
              </Link>
              <a
                href="#contact"
                className={`font-medium transition-colors ${
                  scrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
                }`}
              >
                Contact
              </a>
              <Link
                to="/admin"
                className={`font-medium transition-colors ${
                  scrolled ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
                }`}
              >
                <i className="ri-settings-3-line mr-1"></i>
                Admin
              </Link>
              <a
                href="tel:9407365321"
                className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                (940) 736-5321
              </a>
            </div>
            {/* Mobile menu button */}
            <a href="tel:9407365321" className="md:hidden bg-green-600 text-white px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap">
              Call Now
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/28a1bd124fadae0261e1fdf6f0ad6114.jpg"
            alt="Farm landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 w-full max-w-5xl mx-auto pt-20 md:pt-0">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Premium Refurbished Tractors
          </h1>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            Quality agricultural equipment you can trust. Every tractor thoroughly inspected,
            refurbished, and ready to work.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a
              href="#availability"
              className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              See Availability
            </a>
            <a
              href="tel:9407365321"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Call Us Today
            </a>
          </div>
        </div>
      </section>

      {/* Call Now to See Availability Section */}
      <section id="availability" className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 text-sm font-semibold rounded-full">Current Inventory</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Call Now to See<br />Tractor Availability
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Our inventory changes frequently. Give us a call and we'll walk you through exactly what we have in stock — ready to work tractors at honest prices.
          </p>

          {/* Big Phone CTA */}
          <a
            href="tel:9407365321"
            className="inline-flex items-center gap-4 bg-green-600 text-white px-12 py-6 rounded-2xl text-3xl font-bold hover:bg-green-700 transition-all hover:scale-105 shadow-lg whitespace-nowrap mb-8"
          >
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-phone-fill text-4xl"></i>
            </div>
            (940) 736-5321
          </a>

          <p className="text-sm text-gray-500 mb-16">Mon–Fri: 8 AM – 4 PM &nbsp;•&nbsp; Sat–Sun: Closed</p>

          {/* Supporting info cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-4">
                <i className="ri-truck-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Always Changing</h3>
              <p className="text-sm text-gray-600 leading-relaxed">New tractors arrive regularly. Call to hear what's available right now — stock moves fast.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Fully Refurbished</h3>
              <p className="text-sm text-gray-600 leading-relaxed">Every tractor is inspected and refurbished by our expert technicians before it's offered for sale.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Come See In Person</h3>
              <p className="text-sm text-gray-600 leading-relaxed">Located in Sanger, TX. Schedule a visit to inspect and test drive any tractor we have on the lot.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-green-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-gray-500 mb-2">What We Offer</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Complete Tractor<br />Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8">
            <div className="bg-white rounded-2xl p-8 border-2 border-green-100">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-tools-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Full Refurbishment</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Our expert technicians completely rebuild engines, transmissions, and hydraulic systems. Every component is inspected, repaired, or replaced to factory specifications ensuring years of reliable operation.
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-3/5">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-900 whitespace-nowrap">Customer Reviews</span>
              </div>
              <blockquote className="text-3xl md:text-4xl font-serif text-gray-900 leading-tight mb-6">
                "Best investment for our farm. The refurbishment quality exceeded expectations and the warranty gave us complete peace of mind."
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="font-bold text-gray-900">Michael Thompson</div>
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap">Verified Purchase</span>
              </div>
            </div>

            <div className="w-full lg:w-2/5">
              <div className="w-full h-96 rounded-2xl overflow-hidden">
                <img src="https://static.readdy.ai/image/f322174fe77985532ed178684e9cde9f/d110acef0aa4f570511dc9a1125df44f.jpeg" alt="Customer" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-green-50 p-12 lg:p-16">
            <p className="text-sm text-gray-600 mb-4">Common Questions</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              Everything You<br />
              Need to Know<br />
              About Our Tractors
            </h2>
          </div>

          <div id="faq-details" className="mt-16 max-w-4xl mx-auto">
            <div className="space-y-6">
              <FAQItem
                question="Can I trade in my old tractor?"
                answer="Absolutely! We accept trade-ins on all makes and models. Our team will provide a fair market evaluation of your equipment. Trade-in value can be applied directly to your purchase, simplifying the upgrade process."
              />
              <FAQItem
                question="What does your refurbishment process include?"
                answer="Our 50-point inspection covers every major system. We rebuild engines and transmissions to factory specs, replace worn hydraulic components, update electrical systems, repaint exteriors, and refurbish cabs. Each tractor is test-driven for 20+ hours before sale."
              />
              <FAQItem
                question="Can I inspect the tractor before purchasing?"
                answer="We encourage on-site inspections! Schedule a visit to our facility to see any tractor in person. You can test drive equipment and bring your own mechanic for an independent evaluation. We provide complete service history and inspection reports."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-green-50 to-green-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500 tracking-wider mb-4">GET IN TOUCH</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Find Your<br />
            Perfect Tractor?
          </h2>
          <p className="text-base text-gray-600 leading-relaxed mb-8">
            Our team is ready to help you find the right equipment for your operation. Schedule a consultation to discuss your needs, arrange a facility tour, or get answers to any questions about our inventory and services.
          </p>
          <a href="tel:9407365321" className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white text-sm font-semibold rounded-full hover:bg-green-700 transition-colors mb-6 whitespace-nowrap">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill"></i>
            </div>
            Call (940) 736-5321
          </a>
          <div className="text-sm text-gray-600 mb-6">
            <a href="tel:9407365321" className="hover:text-green-600 transition-colors whitespace-nowrap">Call: (940) 736-5321</a>
            <span className="mx-3">•</span>
            <a href="mailto:sales@4leaftrading.com" className="hover:text-green-600 transition-colors whitespace-nowrap">sales@4leaftrading.com</a>
          </div>

          {/* Business Hours */}
          <div className="bg-white rounded-2xl p-8 border-2 border-green-100 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Business Hours</h3>
            <div className="space-y-3 text-center">
              <div className="py-3 border-b border-gray-100">
                <p className="text-base font-semibold text-gray-900">Monday - Friday</p>
                <p className="text-base text-gray-600 mt-1">8:00 AM – 4:00 PM</p>
              </div>
              <div className="py-3">
                <p className="text-base font-semibold text-gray-900">Saturday - Sunday</p>
                <p className="text-base text-red-600 font-semibold mt-1">Closed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <img src="https://static.readdy.ai/image/f322174fe77985532ed178684e9cde9f/7b841d10159b36d89f70d971de020396.png" alt="Logo" className="h-12 w-auto mb-4" />
              <p className="text-base text-white/80 mb-6">
                Your trusted source for quality used and refurbished tractors in Sanger, Texas. Generations of expertise serving farmers nationwide.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-green-600 hover:border-green-600 transition-colors">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase mb-4">Contact Information</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white/60 mb-1">Address</p>
                  <p className="text-base text-white">6662 Indian Trail<br />Sanger, TX 76266</p>
                </div>
                <div>
                  <p className="text-sm text-white/60 mb-1">Phone</p>
                  <a href="tel:9407365321" className="text-base text-white hover:text-green-400 transition-colors whitespace-nowrap">(940) 736-5321</a>
                </div>
                <div>
                  <p className="text-sm text-white/60 mb-1">Email</p>
                  <a href="mailto:sales@4leaftrading.com" className="text-base text-white hover:text-green-400 transition-colors whitespace-nowrap">sales@4leaftrading.com</a>
                </div>
                <div>
                  <p className="text-sm text-white/60 mb-1">Hours</p>
                  <p className="text-base text-white">Mon-Fri: 8 AM – 4 PM<br />Sat-Sun: Closed</p>
                </div>
              </div>
            </div>

            <div>
              <div className="w-full h-48 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3343.8!2d-97.1!3d33.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDI0JzAwLjAiTiA5N8KwMDYnMDAuMCJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="4 Leaf Trading Location"
                ></iframe>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">© 2025 4 Leaf Trading. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h3>

            <div className="space-y-4">
              <a
                href="tel:9407365321"
                className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-green-600 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-phone-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Call Us</p>
                  <p className="text-sm text-gray-600">(940) 736-5321</p>
                </div>
              </a>

              <a
                href="sms:9407365321"
                className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-green-600 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-message-3-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Text Us</p>
                  <p className="text-sm text-gray-600">Send a message</p>
                </div>
              </a>

              <a
                href="mailto:sales@4leaftrading.com"
                className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-green-600 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-mail-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Email Us</p>
                  <p className="text-sm text-gray-600">sales@4leaftrading.com</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Floating Contact Button */}
      <button
        onClick={() => setShowContactModal(true)}
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-all hover:scale-110 flex items-center justify-center z-40 cursor-pointer"
      >
        <i className="ri-customer-service-2-line text-2xl"></i>
      </button>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors text-left cursor-pointer"
      >
        <h4 className="text-base font-semibold text-gray-900 pr-4">{question}</h4>
        <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl text-gray-600 flex-shrink-0`}></i>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-gray-50">
          <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}
